import { UserProfileIcon } from '@components/avatars';
import { BtnSecondary } from '@components/buttons';
import { timeago } from 'shared/dateutil';
import { Discussion } from './types';

type Props = {
  discussions: Discussion[];
  hasMore: boolean;
  isLoading: boolean;
  onShowMore: () => void;
};

function stripHtml(html: string) {
  return html.replace(/<[^>]*>/g, '');
}

function truncate(text: string, maxLen: number) {
  if (text.length <= maxLen) {
    return text;
  }
  return text.slice(0, maxLen) + '…';
}

function buildViewUrl(d: Discussion) {
  if (d.categoryId) {
    return `/courses/${d.courseId}/discussions/${d.categoryId}/${d.id}`;
  }
  return `/courses/${d.courseId}/discussions`;
}

export function TopDiscussions({ discussions, hasMore, isLoading, onShowMore }: Props) {
  if (discussions.length === 0) {
    return null;
  }

  return (
    <div class="mt-10">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">What's Resonating</h3>
      <div class="flex flex-col gap-3">
        {discussions.map((d) => (
          <DiscussionCard key={d.id} discussion={d} />
        ))}
      </div>
      {hasMore && (
        <div class="text-center mt-6">
          <BtnSecondary isLoading={isLoading} onClick={onShowMore}>
            Show more
          </BtnSecondary>
        </div>
      )}
    </div>
  );
}

function DiscussionCard({ discussion }: { discussion: Discussion }) {
  const snippet = discussion.snippet ? truncate(stripHtml(discussion.snippet), 120) : '';
  const title = discussion.prompt || snippet;
  const viewUrl = buildViewUrl(discussion);

  return (
    <div class="border rounded-lg p-4 flex items-start gap-4">
      <UserProfileIcon
        user={{
          name: discussion.authorName,
          profilePhotoUrl: discussion.authorProfilePhotoUrl,
        }}
        size="w-10 h-10"
      />
      <div class="flex-1 min-w-0">
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0">
            <div class="font-medium text-gray-900">{discussion.authorName}</div>
            {title && <p class="text-sm text-gray-600 mt-1 break-words">"{title}"</p>}
          </div>
          <span class="text-sm font-medium text-gray-500 shrink-0">
            Score: {discussion.engagementScore}
          </span>
        </div>
        <div class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
          <span>
            {discussion.replyCount} repl{discussion.replyCount !== 1 ? 'ies' : 'y'}
          </span>
          <span>
            {discussion.likeCount} like{discussion.likeCount !== 1 ? 's' : ''}
          </span>
          <span>{timeago(discussion.createdAt)}</span>
          <a href={viewUrl} class="text-indigo-600 hover:text-indigo-800">
            View
          </a>
        </div>
      </div>
    </div>
  );
}
